import { Component, OnInit, HostListener } from "@angular/core";
import { User, UsersService } from "@flashvpn-io/web-core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";

type LanguageCode = 'zh-CN' | 'zh-TW' | 'en-US' | 'ru-RU';

interface Language {
    code: string;
    name: string;
}

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css']
})
export class HeaderComponent implements OnInit {

    private readonly flagMap: Record<string, string> = {
        'US': '🇺🇸',
        'JP': '🇯🇵',
        'SG': '🇸🇬',
        'HK': '🇭🇰',
        'TW': '🇹🇼',
        'DE': '🇩🇪',
        'CN': '🇨🇳',
        'RU': '🇷🇺'
    };

    // Map language code to country code for flag
    private getCountryCode(langCode: string): string {
        switch (langCode) {
            case 'zh-CN': return 'CN';
            case 'zh-TW': return 'TW';
            case 'en-US': return 'US';
            case 'ru-RU': return 'RU';
            // Add more as needed
            default: return 'US';
        }
    }

    getFlagEmoji(langCode: string): string {
        const countryCode = this.getCountryCode(langCode);
        return this.flagMap[countryCode] || '';
    }


    public user: User | null = null;
    showLanguageSelector = false;
    showLanguageDropdown = false;
    currentLanguage = '';

    languages: Language[] = [
        { code: 'zh-CN', name: '简体中文' },
        { code: 'zh-TW', name: '繁體中文' },
        { code: 'en-US', name: 'English' },
        { code: 'ru-RU', name: 'Русский' }
    ];

    private readonly languageMap: Record<LanguageCode, string> = {
        'zh-CN': '简体中文',
        'zh-TW': '繁體中文',
        'en-US': 'English',
        'ru-RU': 'Русский'
    };

    constructor(
        private userService: UsersService,
        private router: Router,
        private translate: TranslateService
    ) {
    }

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent) {
        // 点击外部区域时关闭语言下拉菜单
        const target = event.target as HTMLElement;
        if (!target.closest('.language-dropdown')) {
            this.showLanguageDropdown = false;
        }
    }

    logout() {
        this.userService.logout();
        this.user = null;
        this.router.navigate(['/users/signin']);
    }

    toggleLanguageSelector() {
        this.showLanguageSelector = !this.showLanguageSelector;
    }

    toggleLanguageDropdown(event?: MouseEvent) {
        if (event) {
            event.stopPropagation();
        }
        this.showLanguageDropdown = !this.showLanguageDropdown;
    }

    getCurrentLanguageCode(): string {
        return this.translate.currentLang || localStorage.getItem('selectedLanguage') || 'zh-CN';
    }

    selectLanguage(langCode: string) {
        // 切换语言
        this.translate.use(langCode);

        // 保存到本地存储
        localStorage.setItem('selectedLanguage', langCode);

        // 更新当前语言显示
        this.currentLanguage = this.languageMap[langCode as LanguageCode] || langCode;

        // 关闭下拉菜单
        this.showLanguageDropdown = false;

        // 刷新页面以应用新的语言设置
        // 注意：这种方法简单但有效，在实际应用中可能需要更优雅的方式
        window.location.reload();
    }

    onLanguageChange() {
        this.showLanguageSelector = false;
        const currentLang = this.translate.currentLang as LanguageCode;
        this.currentLanguage = this.languageMap[currentLang] || currentLang;
    }

    async ngOnInit() {
        this.user = await this.userService.currentUser();

        // 从本地存储获取保存的语言
        const savedLang = localStorage.getItem('selectedLanguage') as LanguageCode;
        if (savedLang && this.languageMap[savedLang]) {
            this.currentLanguage = this.languageMap[savedLang];
        } else {
            const currentLang = this.translate.currentLang as LanguageCode;
            this.currentLanguage = this.languageMap[currentLang] || currentLang;
        }

        // 监听语言变化
        this.translate.onLangChange.subscribe(() => {
            const currentLang = this.translate.currentLang as LanguageCode;
            this.currentLanguage = this.languageMap[currentLang] || currentLang;
        });
    }
}
